/**
 * Bundle item prices for the Slice Fix System sections.
 *
 * The prices on the bundle metaobjects are plain numbers in the shop currency, so
 * Liquid renders them untouched — it only converts and rounds real product prices.
 * On any other currency this walks every amount marked with data-et-usd-amount and
 * finishes the job the same way the market does: convert at the active rate, round
 * to the market's cents ending, format in the market's own money format. That way
 * an item price lands on €43,95 like the headline instead of drifting to €43,75.
 *
 * The conversion data comes from the nearest [data-et-money] block (see the
 * et-sfs-money-data snippet), so a section works on its own as well as next to the
 * bundle product section.
 *
 * Runs once on load and again on shopify:section:load. Anything that clones marked
 * markup can convert it afterwards with ETSfsMoney.convert(clone).
 */
(function () {
  const patterns = new WeakMap();

  function config(el) {
    const scope = el.closest('.shopify-section') || document;
    const source = scope.querySelector('[data-et-money]') || document.querySelector('[data-et-money]');
    if (!source) return null;

    if (!patterns.has(source)) {
      let parsed = null;
      try {
        const data = JSON.parse(source.textContent);
        parsed = { rounding: data.rounding, pattern: moneyPattern(data.sample) };
      } catch (error) {
        console.error('[et-sfs-money] invalid JSON in [data-et-money]', error);
      }
      patterns.set(source, parsed);
    }

    return patterns.get(source);
  }

  /**
   * Reads the market's money format off the Liquid-rendered sample — 12,345.67 in
   * the market's own format — so "€12.345,67" yields a "." thousands separator and
   * a "," decimal separator, and "¥12,346" yields no decimals at all.
   */
  function moneyPattern(sample) {
    if (!sample) return null;

    const match = String(sample).match(/\d[\d.,'\u00A0\u202F ]*\d|\d/);
    if (!match) return null;

    const number = match[0];
    let integer = number;
    let decimal = '';

    // A separator followed by exactly two digits is the decimal separator
    if (/^[.,]\d\d$/.test(number.slice(-3))) {
      decimal = number.slice(-3).charAt(0);
      integer = number.slice(0, -3);
    }

    return {
      prefix: sample.slice(0, match.index),
      suffix: sample.slice(match.index + number.length),
      group: (integer.match(/\D/) || [''])[0],
      decimal: decimal
    };
  }

  /**
   * Markets can round every converted price to a fixed cents ending, which is why
   * the headline shows €43,95 where a plain conversion lands on €43,75.
   */
  function roundToMarket(cents, ending) {
    if (ending === null || ending === undefined) return Math.round(cents);

    const rounded = Math.round((cents - ending) / 100) * 100 + ending;
    return rounded < ending ? ending : rounded;
  }

  function format(cents, pattern, currencyCode) {
    if (!pattern) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currencyCode || 'USD'
        }).format(cents / 100);
      } catch (error) {
        return String(cents / 100);
      }
    }

    const amount = cents / 100;
    const parts = (pattern.decimal ? amount.toFixed(2) : String(Math.round(amount))).split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, pattern.group);
    const decimals = pattern.decimal ? pattern.decimal + parts[1] : '';

    return pattern.prefix + integer + decimals + pattern.suffix;
  }

  /**
   * @param {ParentNode} [root] element to convert within — defaults to the page
   */
  function convert(root) {
    const currency = window.Shopify && window.Shopify.currency;
    const rate = parseFloat(currency && currency.rate) || 1;
    if (rate === 1) return;

    const scope = root || document;
    const targets = Array.from(scope.querySelectorAll('[data-et-usd-amount]'));

    targets.forEach((el) => {
      if (el.dataset.etUsdConverted === 'true') return;

      const cents = parseFloat(el.dataset.etUsdAmount);
      const settings = config(el);
      if (!cents || !settings) return;

      el.innerHTML = format(roundToMarket(cents * rate, settings.rounding), settings.pattern, currency.active);
      el.dataset.etUsdConverted = 'true';
    });
  }

  window.ETSfsMoney = { convert: convert };

  convert();
  document.addEventListener('shopify:section:load', function () {
    convert();
  });
})();
